/* ============================================================
   Sync status — what the corner badge reads.

   Deliberately its OWN context rather than another field on StoreValue. The
   phase flips on every drain and every pull; folding it into the store value
   would re-identify that object and re-render every screen for a change only
   the badge cares about. Screens read data from `useStore()`, the badge reads
   liveness from `useSync()`, and neither pays for the other.

   The four phases are the only ones the UI can act on. `offline` and `error`
   are kept apart because they mean different things to the retry machinery —
   an offline entry is retried forever, a server rejection is retried a bounded
   number of times (store/outbox.ts → dispositionFor) — even though the badge
   draws both as "not synced".
   ============================================================ */
import { createContext, useContext } from 'react';

export type SyncPhase =
  /** everything the server needs to know, it knows */
  | 'synced'
  /** a drain or a pull is in flight right now */
  | 'syncing'
  /** the request never reached the server — no connection */
  | 'offline'
  /** the server answered, and the answer was a failure */
  | 'error';

export interface SyncStatus {
  phase: SyncPhase;
  /** queued mutations the server hasn't acknowledged yet */
  pending: number;
  /** epoch ms of the last exchange that actually reached the server, or null
     when this launch hasn't managed one */
  lastSyncedAt: number | null;
  /** Flush the queue and pull — the badge's tap target. Never throws and never
     stacks: a tap while a manual sync is already running is a no-op. */
  syncNow: () => void;
}

export const SyncContext = createContext<SyncStatus | null>(null);

export function useSync(): SyncStatus {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within StoreProvider');
  return ctx;
}
